/**
 * Component Node - Konva.js Component Rendering
 *
 * FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/ComponentNode.js
 *
 * Renders SAX components as interactive rectangles with input/output slots on Konva canvas
 */

import Konva from 'konva';

export class ComponentNode {
  constructor(component, config = {}, saxStore = null) {
    this.component = component;
    this.saxStore = saxStore; // Store reference for virtual slots
    this.config = {
      slotRadius: 5, // 10px diameter for connection dots
      slotSpacing: 24, // Increased spacing for better slot separation
      padding: 10,
      fontSize: 12,
      headerHeight: 32, // Slightly taller header for better proportions
      ...config,
    };

    this.group = null;
    this.rect = null;
    this.nameText = null;
    this.typeText = null;
    this.inputSlots = [];
    this.outputSlots = [];
    this.slotPositions = {}; // Store slot positions for link connections
    this.virtualSlots = { inputs: [], outputs: [] }; // Virtual slots for folders

    this.buildNode();
  }

  /**
   * Build the Konva group with all shapes
   */
  buildNode() {
    // Create main group
    this.group = new Konva.Group({
      x: this.component.position.x,
      y: this.component.position.y,
      draggable: true,
    });

    // Compute virtual slots for folders
    if (this.component.type === 'sys::Folder' && this.saxStore && this.saxStore.computeFolderVirtualSlots) {
      this.virtualSlots = this.saxStore.computeFolderVirtualSlots(this.component.id);
    }

    // Calculate proper height based on slots (including virtual slots)
    const inputSlotCount = (this.component.input_slots || []).filter((slot) => !slot.flags || !slot.flags.includes('c')).length;
    const outputSlotCount = (this.component.output_slots || []).filter((slot) => !slot.flags || !slot.flags.includes('c')).length;
    const virtualInputCount = this.virtualSlots.inputs.length;
    const virtualOutputCount = this.virtualSlots.outputs.length;
    const totalSlots = inputSlotCount + outputSlotCount + virtualInputCount + virtualOutputCount;
    const calculatedHeight = this.config.headerHeight + (totalSlots * 24); // 24px per slot row

    // Component rectangle
    this.rect = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.component.width || 120,
      height: calculatedHeight,
      fill: this.component.color || '#3B82F6',
      stroke: '#1E40AF',
      strokeWidth: 2,
      cornerRadius: 8,
      shadowColor: 'rgba(0, 0, 0, 0.15)',
      shadowBlur: 8,
      shadowOpacity: 0.15,
      shadowOffset: { x: 0, y: 2 },
    });

    this.group.add(this.rect);

    // Header background - blue gradient for professional look
    const header = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.rect.width(),
      height: this.config.headerHeight,
      fill: '#2196F3', // Primary blue header
      cornerRadius: [8, 8, 0, 0], // Match component corner radius
    });

    this.group.add(header);

    // Component name text - larger and centered
    this.nameText = new Konva.Text({
      x: 0,
      y: 8,
      text: this.component.name,
      fontSize: 13,
      fontStyle: 'bold',
      fill: 'white',
      width: this.rect.width(),
      align: 'center',
    });

    this.group.add(this.nameText);

    // Component type text - smaller, below name
    this.typeText = new Konva.Text({
      x: 0,
      y: 21,
      text: this.component.type,
      fontSize: 10,
      fill: 'rgba(255, 255, 255, 0.8)',
      width: this.rect.width(),
      align: 'center',
    });

    this.group.add(this.typeText);

    // Add input slots (left side)
    this.addInputSlots();

    // Add output slots (right side)
    this.addOutputSlots();

    // Add event handlers
    this.addEventHandlers();

    return this.group;
  }

  /**
   * Get slot color based on data type
   */
  getSlotColor(slotType) {
    const typeColors = {
      'float': '#4CAF50',   // Green for float (analog values)
      'int': '#2196F3',     // Blue for int (digital values)
      'bool': '#FF9800',    // Orange for bool (binary states)
    };

    return typeColors[slotType] || '#9E9E9E'; // Gray for 'other' or undefined
  }

  /**
   * Add input slots to left side
   */
  addInputSlots() {
    // Filter out config slots (flags: "c") - these are config parameters, not runtime connections
    const allSlots = this.component.input_slots || [];
    const regularSlots = allSlots.filter((slot) => !slot.flags || !slot.flags.includes('c'));

    // Combine regular slots and virtual slots
    const slots = [...regularSlots, ...this.virtualSlots.inputs];

    const startY = this.config.headerHeight;
    const slotRowHeight = 24; // Fixed height for each slot row

    slots.forEach((slot, index) => {
      const y = startY + index * slotRowHeight;
      const isVirtual = slot.virtual === true;

      // Slot row background (for hover effect - transparent by default)
      const rowBg = new Konva.Rect({
        x: 0,
        y: y,
        width: this.rect.width(),
        height: slotRowHeight,
        fill: 'transparent',
      });
      this.group.add(rowBg);

      // Add slot row separator (border between slots)
      if (index < slots.length - 1) {
        const separator = new Konva.Line({
          points: [0, y + slotRowHeight, this.rect.width(), y + slotRowHeight],
          stroke: 'rgba(255, 255, 255, 0.15)',
          strokeWidth: 1,
        });
        this.group.add(separator);
      }

      // Slot circle - centered on left edge (half in, half out)
      // Color based on data type (float=green, int=blue, bool=orange, other=gray)
      // Virtual slots have lighter color and dashed border
      const slotColor = this.getSlotColor(slot.type);
      const circle = new Konva.Circle({
        x: 0, // Centered on left edge
        y: y + slotRowHeight / 2,
        radius: this.config.slotRadius,
        fill: isVirtual ? slotColor + '80' : slotColor, // 50% opacity for virtual
        stroke: '#FFFFFF',
        strokeWidth: 2,
        dash: isVirtual ? [3, 3] : undefined, // Dashed border for virtual slots
      });

      // Slot label - centered in slot row
      // Use displayName for virtual slots (contains child component name)
      const label = new Konva.Text({
        x: 0,
        y: y + slotRowHeight / 2 - 6,
        text: isVirtual ? slot.displayName : slot.name,
        fontSize: isVirtual ? 10 : 11, // Slightly smaller for virtual slots
        fontWeight: '500',
        fill: isVirtual ? 'rgba(255, 255, 255, 0.8)' : 'white',
        width: this.rect.width(),
        align: 'center',
      });

      this.group.add(circle);
      this.group.add(label);

      this.inputSlots.push(circle);

      // Store slot position for link connections
      // For virtual slots, use the full qualified name as the key
      const slotKey = isVirtual ? slot.name : slot.name;
      this.slotPositions[slotKey] = {
        type: 'input',
        x: 0, // Centered on edge
        y: y + slotRowHeight / 2,
        circle: circle,
        slot: slot,
        rowBg: rowBg,
        isVirtual: isVirtual,
      };
    });
  }

  /**
   * Add output slots to right side
   */
  addOutputSlots() {
    // Filter out config slots (flags: "c") - these are config parameters, not runtime connections
    const allSlots = this.component.output_slots || [];
    const regularSlots = allSlots.filter((slot) => !slot.flags || !slot.flags.includes('c'));

    // Combine regular slots and virtual slots
    const slots = [...regularSlots, ...this.virtualSlots.outputs];

    // Calculate starting Y position - after input slots (including virtual inputs)
    const inputSlotCount = (this.component.input_slots || []).filter((slot) => !slot.flags || !slot.flags.includes('c')).length;
    const virtualInputCount = this.virtualSlots.inputs.length;
    const startY = this.config.headerHeight + ((inputSlotCount + virtualInputCount) * 24);
    const slotRowHeight = 24;
    const width = this.rect.width();

    slots.forEach((slot, index) => {
      const y = startY + index * slotRowHeight;
      const isVirtual = slot.virtual === true;

      // Slot row background (for hover effect - transparent by default)
      const rowBg = new Konva.Rect({
        x: 0,
        y: y,
        width: this.rect.width(),
        height: slotRowHeight,
        fill: 'transparent',
      });
      this.group.add(rowBg);

      // Add slot row separator (border between slots)
      if (index < slots.length - 1) {
        const separator = new Konva.Line({
          points: [0, y + slotRowHeight, this.rect.width(), y + slotRowHeight],
          stroke: 'rgba(255, 255, 255, 0.15)',
          strokeWidth: 1,
        });
        this.group.add(separator);
      }

      // Slot circle - centered on right edge (half in, half out)
      // Color based on data type (float=green, int=blue, bool=orange, other=gray)
      // Virtual slots have lighter color and dashed border
      const slotColor = this.getSlotColor(slot.type);
      const circle = new Konva.Circle({
        x: width, // Centered on right edge
        y: y + slotRowHeight / 2,
        radius: this.config.slotRadius,
        fill: isVirtual ? slotColor + '80' : slotColor, // 50% opacity for virtual
        stroke: '#FFFFFF',
        strokeWidth: 2,
        dash: isVirtual ? [3, 3] : undefined, // Dashed border for virtual slots
      });

      // Slot label - centered in slot row
      // Use displayName for virtual slots (contains child component name)
      const label = new Konva.Text({
        x: 0,
        y: y + slotRowHeight / 2 - 6,
        text: isVirtual ? slot.displayName : slot.name,
        fontSize: isVirtual ? 10 : 11, // Slightly smaller for virtual slots
        fontWeight: '500',
        fill: isVirtual ? 'rgba(255, 255, 255, 0.8)' : 'white',
        width: this.rect.width(),
        align: 'center',
      });

      this.group.add(circle);
      this.group.add(label);

      this.outputSlots.push(circle);

      // Store slot position for link connections
      // For virtual slots, use the full qualified name as the key
      const slotKey = isVirtual ? slot.name : slot.name;
      this.slotPositions[slotKey] = {
        type: 'output',
        x: width, // Centered on edge
        y: y + slotRowHeight / 2,
        circle: circle,
        slot: slot,
        rowBg: rowBg,
        isVirtual: isVirtual,
      };
    });
  }

  /**
   * Add event handlers
   */
  addEventHandlers() {
    // Hover effects
    this.group.on('mouseenter', () => {
      document.body.style.cursor = 'move';
      this.rect.strokeWidth(3);
    });

    this.group.on('mouseleave', () => {
      document.body.style.cursor = 'default';
      if (!this.isSelected()) {
        this.rect.strokeWidth(2);
      }
    });

    // Slot hover effects - enhanced with glow and row highlighting
    [...this.inputSlots, ...this.outputSlots].forEach((slot) => {
      slot.on('mouseenter', () => {
        document.body.style.cursor = 'crosshair';
        slot.scale({ x: 1.3, y: 1.3 });
        slot.shadowColor('rgba(0, 0, 0, 0.3)');
        slot.shadowBlur(8);
        slot.getLayer()?.batchDraw();
      });

      slot.on('mouseleave', () => {
        document.body.style.cursor = 'default';
        slot.scale({ x: 1, y: 1 });
        slot.shadowColor('transparent');
        slot.shadowBlur(0);
        slot.getLayer()?.batchDraw();
      });
    });

    // Add hover effects to slot row backgrounds
    Object.values(this.slotPositions).forEach((slotInfo) => {
      if (slotInfo.rowBg) {
        slotInfo.rowBg.on('mouseenter', () => {
          slotInfo.rowBg.fill('rgba(255, 255, 255, 0.05)');
          slotInfo.rowBg.getLayer()?.batchDraw();
        });

        slotInfo.rowBg.on('mouseleave', () => {
          slotInfo.rowBg.fill('transparent');
          slotInfo.rowBg.getLayer()?.batchDraw();
        });
      }
    });
  }

  /**
   * Get absolute position of a slot (for link connections)
   */
  getSlotPosition(slotName) {
    const slotInfo = this.slotPositions[slotName];
    if (!slotInfo) {return null;}

    // Use position() instead of getAbsolutePosition() to get coordinates
    // relative to the parent layer (not affected by stage scale/zoom)
    const groupPos = this.group.position();

    return {
      x: groupPos.x + slotInfo.x,
      y: groupPos.y + slotInfo.y,
      type: slotInfo.type,
    };
  }

  /**
   * Set selected state
   */
  setSelected(selected) {
    if (selected) {
      this.rect.stroke('#EF4444');
      this.rect.strokeWidth(3);
      this.rect.shadowColor('red');
      this.rect.shadowBlur(15);
    } else {
      this.rect.stroke('#1E40AF');
      this.rect.strokeWidth(2);
      this.rect.shadowColor('black');
      this.rect.shadowBlur(10);
    }

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Check if selected
   */
  isSelected() {
    return this.rect.stroke() === '#EF4444';
  }

  /**
   * Update component data
   */
  updateComponent(component) {
    this.component = component;

    // Update position
    this.group.position({
      x: component.position.x,
      y: component.position.y,
    });

    // Update name
    this.nameText.text(component.name);

    // Update type
    this.typeText.text(component.type);

    // Update color
    this.rect.fill(component.color || '#3B82F6');

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Get current position
   */
  getPosition() {
    return this.group.position();
  }

  /**
   * Set position
   */
  setPosition(x, y) {
    this.group.position({ x, y });
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Show/hide component
   */
  setVisible(visible) {
    this.group.visible(visible);
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Destroy the node
   */
  destroy() {
    this.group.destroy();
  }

  /**
   * Get the Konva group
   */
  getGroup() {
    return this.group;
  }

  /**
   * Get component ID
   */
  getId() {
    return this.component.id;
  }

  /**
   * Get component data WITH ACTUAL RENDERED DIMENSIONS
   * CRITICAL: Returns the actual visual size, not the stored size
   * The visual component height is calculated based on slots, not stored height
   */
  getComponent() {
    return {
      ...this.component,
      // Override with ACTUAL rendered dimensions from Konva rect
      width: this.rect.width(),
      height: this.rect.height(),
    };
  }

  /**
   * Bring to front
   */
  moveToTop() {
    this.group.moveToTop();
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Send to back
   */
  moveToBottom() {
    this.group.moveToBottom();
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Get all slot names
   */
  getAllSlotNames() {
    return Object.keys(this.slotPositions);
  }

  /**
   * Get slot info
   */
  getSlotInfo(slotName) {
    return this.slotPositions[slotName];
  }

  /**
   * Highlight slot (for link creation)
   */
  highlightSlot(slotName, highlight = true) {
    const slotInfo = this.slotPositions[slotName];
    if (!slotInfo) {return;}

    if (highlight) {
      slotInfo.circle.stroke('#FFFFFF');
      slotInfo.circle.strokeWidth(3);
      slotInfo.circle.scale({ x: 1.5, y: 1.5 });
    } else {
      const color = slotInfo.type === 'input' ? '#047857' : '#D97706';
      slotInfo.circle.stroke(color);
      slotInfo.circle.strokeWidth(2);
      slotInfo.circle.scale({ x: 1, y: 1 });
    }

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Mark slot as connected (yellow fill)
   */
  markSlotConnected(slotName, connected = true) {
    const slotInfo = this.slotPositions[slotName];
    if (!slotInfo) {return;}

    if (connected) {
      // Yellow fill for connected state
      slotInfo.circle.fill('#FFD700');
      slotInfo.circle.stroke('#FFA500');
    } else {
      // Restore original color
      const fillColor = slotInfo.type === 'input' ? '#10B981' : '#F59E0B';
      const strokeColor = slotInfo.type === 'input' ? '#047857' : '#D97706';
      slotInfo.circle.fill(fillColor);
      slotInfo.circle.stroke(strokeColor);
    }

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Clone the component node
   */
  clone(newId, offset = { x: 20, y: 20 }) {
    const newComponent = {
      ...this.component,
      id: newId,
      position: {
        x: this.component.position.x + offset.x,
        y: this.component.position.y + offset.y,
      },
    };

    return new ComponentNode(newComponent, this.config);
  }

  /**
   * Update component name
   */
  updateName(newName) {
    this.component.name = newName;
    if (this.nameText) {
      this.nameText.text(newName);
    }
  }
}

export default ComponentNode;
